import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AnimatedWaves } from "@/components/AnimatedWaves";
import { PasswordInput } from "@/components/PasswordInput";

export function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }

    // Mirror the server rule so the user gets a specific message instead of a
    // generic "Datos de entrada inválidos" 400.
    if (password.length < 12 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError(t("auth.passwordRule"));
      return;
    }

    if (!token) {
      setError(t("auth.resetTokenMissing"));
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login", { state: { resetSuccess: true }, replace: true });
    } catch (err) {
      const msg =
        (err as AxiosError<{ message: string }>).response?.data?.message ??
        t("auth.errorGeneric");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient text-white px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-ocean-mist opacity-60" aria-hidden="true" />
      <AnimatedWaves position="bottom" tone="light" />

      <Link
        to="/login"
        className="absolute top-6 left-6 text-sm text-primary-100 hover:text-white tracking-wider uppercase z-10"
      >
        ← {tr("Volver al inicio de sesión", "Back to login")}
      </Link>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="stacked" color="white" className="text-lg" />
          </div>
          <p className="text-primary-100/80 text-sm tracking-wider uppercase">{t("app.tagline")}</p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-elegant-lg p-8 border border-white/40">
          <h2 className="font-serif text-2xl text-primary-800 mb-1">
            {t("auth.resetPasswordTitle")}
          </h2>
          <div className="w-10 h-0.5 bg-dorado mb-6" />

          {!token ? (
            <div className="space-y-4">
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-3 leading-relaxed">
                {t("auth.resetTokenMissing")}
              </div>
              <Link
                to="/forgot-password"
                className="block w-full text-center px-6 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-md font-medium tracking-wide transition shadow-elegant"
              >
                {t("auth.requestNewLink")}
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("auth.resetPasswordInstructions")}
              </p>
              <div>
                <label className="label">{t("auth.newPassword")}</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                <p className="text-xs text-slate-500 mt-1">{t("auth.passwordRule")}</p>
              </div>
              <div>
                <label className="label">{t("auth.confirmPassword")}</label>
                <PasswordInput
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-md font-medium tracking-wide transition shadow-elegant disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? t("auth.resetting") : t("auth.resetPasswordButton")}
              </button>
              <div className="text-center text-sm pt-1">
                <Link to="/login" className="text-primary-600 hover:text-primary-800 hover:underline">
                  {t("auth.backToLogin")}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
